import React, { useEffect, useState, useCallback, useMemo, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import FloatingContact from './components/FloatingContact/FloatingContact';
import Preloader from './components/Preloader/Preloader';
import useStackGuard from './hooks/useStackGuard';
import { markAppReady } from './utils/appReady';
import { PageTransitionProvider } from './components/PageTransition/PageTransition';
import AdminLogin from './admin/AdminLogin';
import AdminLayout from './admin/AdminLayout';
import Dashboard from './admin/Dashboard';
import Clients from './admin/Clients';
import Income from './admin/Income';
import Payments from './admin/Payments';
import Projects from './admin/Projects';
import Logos from './admin/Logos';

import translations from './translations.json';

// Dizajn sistem se ucitava pre svih komponenti da tokeni postoje kad
// komponentni CSS pokusa da ih procita.
import './styles/tokens.css';
import './styles/base.css';
import './styles/ui.css';

const Home = lazy(() => import('./pages/Home'));
const Usluge = lazy(() => import('./pages/Usluge'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const Proces = lazy(() => import('./pages/Proces'));
const Cene = lazy(() => import('./pages/Cene'));
const Contact = lazy(() => import('./pages/contact/Contact'));
const NotFound = lazy(() => import('./pages/NotFound'));

export const LanguageContext = React.createContext(null);

const LANG_KEY = 'mstech_lang';
const SUPPORTED = ['sr', 'en'];

function readInitialLang() {
  try {
    const saved = localStorage.getItem(LANG_KEY);
    if (SUPPORTED.includes(saved)) return saved;
  } catch { /* privatni rezim */ }
  return 'sr';
}

/** Navbar/Footer/FloatingContact se ne prikazuju na admin rutama. */
function Layout({ children }) {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/mstechs');

  if (isAdmin) return children;

  return (
    <>
      <a className="skip-link" href="#main">Preskoči na sadržaj</a>
      <Navbar />
      <main id="main">{children}</main>
      <Footer />
      <FloatingContact />
    </>
  );
}

/** Javlja preloaderu da je sadrzaj stvarno montiran, pa moze da se skloni. */
function RouteReady() {
  useEffect(() => { markAppReady(); }, []);
  return null;
}

/** Rezerva dok se lazy stranica ucitava — bez teksta, samo tanka traka. */
function RouteFallback() {
  return <div className="route-fallback" role="status" aria-label="Učitavanje" />;
}

export default function App() {
  const [language, setLanguage] = useState(readInitialLang);

  // Skida lepljivost sekcijama koje ne staju u prozor — inače bi im
  // donji deo (npr. dugmad u cenovniku) ostao nedostupan.
  useStackGuard();

  // Jezik se pamti i odrazava na <html lang> radi citaca ekrana i pretrazivaca
  useEffect(() => {
    try { localStorage.setItem(LANG_KEY, language); } catch { /* ignore */ }
    document.documentElement.lang = language;
  }, [language]);

  const toggleLanguage = useCallback(() => {
    setLanguage((prev) => (prev === 'sr' ? 'en' : 'sr'));
  }, []);

  const getTranslation = useCallback(
    (section) => translations[section]?.[language] ?? {},
    [language]
  );

  const ctx = useMemo(
    () => ({ language, setLanguage, toggleLanguage, getTranslation, translations }),
    [language, toggleLanguage, getTranslation]
  );

  return (
    <LanguageContext.Provider value={ctx}>
      <Preloader />
      <Router>
        <ScrollToTop />
        <div className="App">
          <PageTransitionProvider>
            <Layout>
            <Suspense fallback={<RouteFallback />}>
              <RouteReady />
              <Routes>
                {/* --- Admin --- */}
                <Route path="/mstechs" element={<AdminLogin />} />
                <Route path="/mstechs/*" element={<AdminLayout />}>
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="clients" element={<Clients />} />
                  <Route path="income" element={<Income />} />
                  <Route path="payments" element={<Payments />} />
                  <Route path="projects" element={<Projects />} />
                  <Route path="logos" element={<Logos />} />
                </Route>

                {/* --- Javne stranice --- */}
                <Route path="/" element={<Home />} />
                <Route path="/usluge" element={<Usluge />} />
                <Route path="/nas-rad" element={<Portfolio />} />
                <Route path="/proces" element={<Proces />} />
                <Route path="/cene" element={<Cene />} />
                <Route path="/kontakt" element={<Contact />} />

                {/* --- Stare rute -> nova struktura ---
                    Sest zasebnih stranica usluga objedinjeno je u /usluge,
                    a /o-nama u sekciju na pocetnoj. Redirekti cuvaju stare
                    linkove koji su vec podeljeni ili indeksirani. */}
                <Route path="/usluge/web-sajtovi" element={<Navigate to="/usluge#web" replace />} />
                <Route path="/usluge/mobilne-aplikacije" element={<Navigate to="/usluge#mobilne" replace />} />
                <Route path="/usluge/drustvene-mreze" element={<Navigate to="/usluge#drustvene" replace />} />
                {/* Edukacija, online radio i dizajn nisu medju uslugama u novom
                    dizajnu, pa ove rute vode na vrh /usluge — sidro koje ne
                    postoji ostavilo bi posetioca na praznom mestu. */}
                <Route path="/usluge/it-skola" element={<Navigate to="/usluge" replace />} />
                <Route path="/usluge/online-radio" element={<Navigate to="/usluge" replace />} />
                <Route path="/usluge/dizajn" element={<Navigate to="/usluge" replace />} />
                <Route path="/o-nama" element={<Navigate to="/#o-nama" replace />} />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            </Layout>
          </PageTransitionProvider>
        </div>
      </Router>
    </LanguageContext.Provider>
  );
}
